import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Download,
  Send,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const SalaryIncrementEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginuserid =
    localStorage.getItem("loginuserid") || localStorage.getItem("userId") || "";

  const screenBadge = useMemo(() => "INFRA SCREEN : C-10/1", []);

  // filters
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("Select");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const month = "May";

  // UI
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // grid (all rows from API)
  const [rows, setRows] = useState([]);

  // search + pagination
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${
    isDark ? "text-gray-200" : "text-gray-700"
  }`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const submitBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-emerald-600 hover:bg-emerald-700");

  const yearsList = useMemo(() => {
    const base = [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022];
    const cy = new Date().getFullYear();
    if (!base.includes(cy)) base.push(cy);
    base.sort((a, b) => a - b);
    return base.map(String);
  }, []);

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchBranches = async () => {
    if (!String(finYearId).trim()) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/api/salary-increment-entry/branches`,
        { params: { finYearId: Number(finYearId) } },
      );
      if (res?.data?.success) setBranches(res.data.rows || []);
      else toast.error(res?.data?.message || "Failed to load branches");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load branches",
      );
    }
  };

  const fetchList = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!year) {
      toast.warn("Please select Year.");
      return;
    }

    try {
      setLoading(true);
      const br = branch === "Select" ? "%" : branch;

      const res = await axios.get(
        `${BASE_URL}/api/salary-increment-entry/list`,
        {
          params: {
            finYearId: Number(finYearId),
            branch: br,
            year,
            month,
          },
        },
      );

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setSelectedIds([]);
        setSelectAll(false);
        setSearchText("");
        setPage(1);
      } else {
        toast.error(res?.data?.message || "Failed to load list");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load list",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    const storedBranch =
      localStorage.getItem("branchid") || localStorage.getItem("branchId");
    if (storedBranch) setBranch(String(storedBranch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (String(finYearId).trim()) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finYearId]);

  // -------------------------
  // SEARCH + PAGINATION (client-side)
  // -------------------------
  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = [r.employee, r.employeeCode, r.designationName, r.branchName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, searchText]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    // keep page valid when search/pageSize changes
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  // -------------------------
  // selection should respect filtered set + current page
  // -------------------------
  const toggleSelect = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((x) => x !== employeeId)
        : [...prev, employeeId],
    );
  };

  const handleSelectAllCurrentPage = () => {
    const idsOnPage = pagedRows.map((r) => r.employeeId);

    if (selectAll) {
      setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
      setSelectAll(false);
      return;
    }

    setSelectedIds((prev) => {
      const set = new Set(prev);
      idsOnPage.forEach((id) => set.add(id));
      return Array.from(set);
    });
    setSelectAll(true);
  };

  useEffect(() => {
    // update selectAll checkbox based on page rows
    if (pagedRows.length === 0) {
      setSelectAll(false);
      return;
    }
    const idsOnPage = pagedRows.map((r) => r.employeeId);
    const allChecked = idsOnPage.every((id) => selectedIds.includes(id));
    setSelectAll(allChecked);
  }, [pagedRows, selectedIds]);

  // mimic ASP TextChanged: totals = gross + hike
  const updateHike = (employeeId, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.employeeId !== employeeId) return r;

        const hikeVal = value === "" ? 0 : safeNum(value);

        const next = { ...r };
        if (field === "hikeSalary") next.hikeSalary = hikeVal;
        if (field === "hikeBata") next.hikeBata = hikeVal;

        next.totalSalary = safeNum(next.grossSalary) + safeNum(next.hikeSalary);
        next.totalBata = safeNum(next.grossBata) + safeNum(next.hikeBata);

        return next;
      }),
    );
  };

  const handleSubmitSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.warn("Select any one..");
      return;
    }

    const ok = window.confirm("Are you sure to submit?");
    if (!ok) return;

    const selectedRows = rows.filter((r) => selectedIds.includes(r.employeeId));

    const payload = {
      finYearId: Number(finYearId),
      userId: Number(loginuserid) || undefined,
      year,
      month,
      items: selectedRows.map((r) => ({
        employeeId: r.employeeId,
        branchId: r.branchId,
        grossSalary: safeNum(r.grossSalary),
        hikeSalary: safeNum(r.hikeSalary),
        totalSalary: safeNum(r.totalSalary),
        grossBata: safeNum(r.grossBata),
        hikeBata: safeNum(r.hikeBata),
        totalBata: safeNum(r.totalBata),
      })),
    };

    try {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/api/salary-increment-entry/submit`,
        payload,
        {
          headers: loginuserid
            ? { "x-loginuserid": String(loginuserid) }
            : undefined,
        },
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Updated Successfully..");
        navigate("/payroll/SalaryIncrementListing");
      } else {
        toast.error(res?.data?.message || "Submit failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Submit failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!year) {
      toast.warn("Please select Year.");
      return;
    }

    try {
      const br = branch === "Select" ? "%" : branch;

      const res = await axios.get(
        `${BASE_URL}/api/salary-increment-entry/excel`,
        {
          params: {
            finYearId: Number(finYearId),
            branch: br,
            year,
            month,
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PREPARE_SALARY_INCREMENT_${year}_to_${Number(year) + 1}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Excel download failed",
      );
    }
  };

  // ---------- Pagination buttons (compact)
  const pageButtons = useMemo(() => {
    const maxBtns = 7;
    const pages = [];
    if (totalPages <= maxBtns) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    const left = Math.max(2, page - 2);
    const right = Math.min(totalPages - 1, page + 2);

    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);

    return pages;
  }, [page, totalPages]);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              SALARY INCREMENT ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <button
          onClick={() => navigate("/payroll/salary-increment-listing")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2
                  className={isDark ? "text-white" : "text-gray-700"}
                />
              </div>

              <div>
                <div
                  className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Salary Increment Entry
                </div>
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Search • Pagination • Prepare increments • Submit
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={fetchList}
                className={ghostBtn}
                disabled={loading}
              >
                <RefreshCcw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <button
                onClick={handleExcel}
                className={ghostBtn}
                disabled={loading}
              >
                <Download className="w-4 h-4" />
                Excel
              </button>

              <button
                onClick={() => setShowFilters((v) => !v)}
                className={ghostBtn}
              >
                {showFilters ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`mt-2 ${inputBase}`}
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => {
                      const val =
                        b.fld_Branchid ??
                        b.branchid ??
                        b.BranchId ??
                        b.value ??
                        b.Value;
                      const text =
                        b.fld_BranchName ??
                        b.branchname ??
                        b.BranchName ??
                        b.text ??
                        b.Text ??
                        String(val);
                      return (
                        <option key={idx} value={String(val)}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>
                    Date (Year) <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`mt-2 ${inputBase}`}
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {yearsList.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <button
                  onClick={fetchList}
                  className={primaryBtn}
                  disabled={loading}
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
          )}

          {/* Search + Page size */}
          <div className={cardBase + " p-5 mb-6"}>
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="flex-1">
                <label className={labelBase}>
                  Search (Name / Code / Designation / Location)
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Type to search..."
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>

              <div className="w-full lg:w-48">
                <label className={labelBase}>Page Size</label>
                <select
                  className={`mt-2 ${inputBase}`}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 15, 20, 30, 50, 100].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table (WIDER + scroll) */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              {/* ✅ make it wide like ASP */}
              <table className="min-w-[1500px] w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap w-[70px]">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-left font-bold whitespace-nowrap w-[220px]">
                      Employee Name
                    </th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap w-[140px]">
                      Employee Code
                    </th>
                    <th className="px-4 py-4 text-left font-bold whitespace-nowrap w-[220px]">
                      Designation
                    </th>
                    <th className="px-4 py-4 text-left font-bold whitespace-nowrap w-[220px]">
                      Location
                    </th>

                    <th className="px-4 py-4 text-right font-bold whitespace-nowrap w-[140px]">
                      GrossSalary
                    </th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap w-[140px]">
                      Hike
                    </th>
                    <th className="px-4 py-4 text-right font-bold whitespace-nowrap w-[150px]">
                      TotalSalary
                    </th>

                    <th className="px-4 py-4 text-right font-bold whitespace-nowrap w-[140px]">
                      GrossBata
                    </th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap w-[140px]">
                      HikeBata
                    </th>
                    <th className="px-4 py-4 text-right font-bold whitespace-nowrap w-[150px]">
                      TotalBata
                    </th>

                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap w-[160px]">
                      <div className="flex items-center justify-center gap-2">
                        Select Page
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAllCurrentPage}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr
                        key={idx}
                        className={isDark ? "bg-gray-900" : "bg-white"}
                      >
                        {Array.from({ length: 12 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pagedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => (
                      <tr
                        key={r.employeeId}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {r.employee}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {r.employeeCode}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {r.designationName}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {r.branchName}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {safeNum(r.grossSalary).toFixed(2)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          {/* ✅ wider input */}
                          <input
                            type="number"
                            value={r.hikeSalary}
                            onChange={(e) =>
                              updateHike(
                                r.employeeId,
                                "hikeSalary",
                                e.target.value,
                              )
                            }
                            className={`w-[120px] text-center ${inputBase}`}
                          />
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {safeNum(r.totalSalary).toFixed(2)}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {safeNum(r.grossBata).toFixed(2)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={r.hikeBata}
                            onChange={(e) =>
                              updateHike(
                                r.employeeId,
                                "hikeBata",
                                e.target.value,
                              )
                            }
                            className={`w-[120px] text-center ${inputBase}`}
                          />
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}
                        >
                          {safeNum(r.totalBata).toFixed(2)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.employeeId)}
                            onChange={() => toggleSelect(r.employeeId)}
                            className="w-4 h-4"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: showing + pagination + submit */}
          <div className="flex flex-col lg:flex-row items-center justify-between mt-6 gap-4">
            <div
              className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of{" "}
              <b>{total}</b> • Selected: <b>{selectedIds.length}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-3 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                    title="Prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {pageButtons.map((p, idx) =>
                    p === "..." ? (
                      <span
                        key={`dots-${idx}`}
                        className="px-3 py-2 text-sm font-bold text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-4 py-2 text-sm font-bold transition ${
                          page === p
                            ? `${primaryBtnClass} text-white`
                            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-3 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                    title="Next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmitSelected}
                disabled={selectedIds.length === 0 || loading}
                className={submitBtn}
              >
                <Send className="w-4 h-4" />
                Submit Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SalaryIncrementEntry;
